import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Waits for every <img> inside the element to finish loading/decoding.
 * Without this, html2canvas can capture the canvas before a not-yet-loaded
 * image (e.g. a QR code that isn't cached like the logo is) has painted,
 * producing a blank/white box for that image in the downloaded PDF.
 */
const waitForImages = (element) => {
  const images = Array.from(element.querySelectorAll('img'));
  return Promise.all(
    images.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }),
  );
};

/**
 * html2canvas sometimes fails to draw a particular <img> at all (renders it
 * as a blank box) even though the image is loaded and paints fine in the
 * live page - a QR code has hit this in practice while a logo right next to
 * it renders correctly. Rather than depend on html2canvas's image handling,
 * re-draw every <img> in the source element directly onto html2canvas's
 * output canvas afterwards, using each image's real on-page position. This
 * guarantees the final canvas always shows the actual image regardless of
 * whether html2canvas itself rendered it.
 */
const patchImagesOntoCanvas = (element, canvas) => {
  const contentRect = element.getBoundingClientRect();
  const scaleX = canvas.width / contentRect.width;
  const scaleY = canvas.height / contentRect.height;
  const ctx = canvas.getContext('2d');

  // canvas.getContext('2d') returns html2canvas's own context object, which
  // it leaves with a non-identity transform after rendering (its internal
  // device-pixel scale + a translate offset). Coordinates below are already
  // scaled to canvas pixel space, so draw through the identity transform or
  // every patched image ends up composited far outside the visible canvas.
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  Array.from(element.querySelectorAll('img')).forEach((img) => {
    if (!img.naturalWidth) return;
    const rect = img.getBoundingClientRect();
    const x = (rect.left - contentRect.left) * scaleX;
    const y = (rect.top - contentRect.top) * scaleY;
    const w = rect.width * scaleX;
    const h = rect.height * scaleY;
    if (w <= 0 || h <= 0) return;

    // Match the visible box's white background (these logos/QRs sit in a
    // white card) so any transparency in the source PNG composites the
    // same way it does on screen instead of showing checkerboard/black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, w, h);
    ctx.drawImage(img, x, y, w, h);
  });

  ctx.restore();
};

/**
 * Renders a DOM element to a paginated A4 PDF and triggers a download.
 * Shared by every styled procurement/receipt document view.
 */
export const downloadElementAsPdf = async (elementId, fileName) => {
  const element = document.getElementById(elementId);
  if (!element) throw new Error(`Element #${elementId} not found`);

  await waitForImages(element);

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: document.documentElement.offsetWidth,
  });

  patchImagesOntoCanvas(element, canvas);

  const imgData = canvas.toDataURL('image/png');

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  if (imgHeight <= pageHeight) {
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
  } else {
    let position = 0;
    let remainingHeight = imgHeight;
    let pageIndex = 0;

    while (remainingHeight > 0) {
      const sourceY = (position / imgHeight) * canvas.height;
      const sliceHeightPx = Math.min((pageHeight / imgHeight) * canvas.height, canvas.height - sourceY);

      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext('2d');

      ctx.drawImage(canvas, 0, sourceY, canvas.width, sliceHeightPx, 0, 0, sliceCanvas.width, sliceCanvas.height);

      const sliceData = sliceCanvas.toDataURL('image/png');

      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(sliceData, 'PNG', 0, 0, imgWidth, (sliceHeightPx / canvas.width) * imgWidth);

      position += pageHeight;
      remainingHeight -= pageHeight;
      pageIndex += 1;
    }
  }

  pdf.save(fileName);
};
